import React from 'react';

export interface HelpItem {
  id: string;
  category: string;
  title: string;
  content: React.ReactNode;
  keywords?: string[];
}

function BlogLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary underline underline-offset-2 hover:opacity-80"
    >
      {children}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M15 3h6v6" />
        <path d="M10 14 21 3" />
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      </svg>
    </a>
  );
}

export const helpData: HelpItem[] = [
  {
    id: 'getting-started',
    category: 'Getting Started',
    title: 'Introduction to Meshpit',
    keywords: ['intro', 'overview', 'beginner', 'start', 'tutorial'],
    content: (
      <div className="space-y-2">
        <p>
          Meshpit is a platform for creating, sharing, and exploring parametric
          OpenSCAD models. It combines a powerful code editor with a real-time
          3D viewer.
        </p>
        <p>
          You can write standard OpenSCAD code, and Meshpit automatically
          generates UI controls for your variables, making it easy to customize
          models without touching the code.
        </p>
        <p>
          New here?{' '}
          <BlogLink href="/blog/getting-started">
            Getting Started with Parametric OpenSCAD
          </BlogLink>{' '}
          walks through the basics — primitives, booleans, and your first
          parametric model. Or follow the hands-on{' '}
          <BlogLink href="/blog/name-tag-tutorial">Name Tag tutorial</BlogLink>{' '}
          to build a printable part step by step.
        </p>
      </div>
    ),
  },
  {
    id: 'editor-basics',
    category: 'Editor',
    title: 'Code Editor',
    keywords: ['editor', 'monaco', 'autocomplete', 'format', 'vim', 'syntax'],
    content: (
      <div className="space-y-2">
        <p>
          The editor supports syntax highlighting, autocomplete, and error
          checking for OpenSCAD.
        </p>
        <ul className="list-disc space-y-1 pl-4">
          <li>
            <strong>Autocomplete:</strong> Press{' '}
            <kbd className="rounded bg-muted px-1">Ctrl</kbd> +{' '}
            <kbd className="rounded bg-muted px-1">Space</kbd> to see available
            modules and functions.
          </li>
          <li>
            <strong>Formatting:</strong> Right-click and select "Format
            Document" to clean up your code.
          </li>
          <li>
            <strong>Vim Mode:</strong> meaningful for power users who prefer
            keyboard-only navigation.
          </li>
        </ul>
        <p>
          Meshpit bundles several OpenSCAD libraries (BOSL2, MCAD, and more) so
          you can use <code>include</code> and <code>use</code> without any
          setup. See the{' '}
          <BlogLink href="/blog/libraries">Bundled Libraries guide</BlogLink>{' '}
          for the full list and usage examples.
        </p>
      </div>
    ),
  },
  {
    id: 'viewer-controls',
    category: 'Viewer',
    title: 'Camera Controls',
    keywords: [
      'camera',
      'orbit',
      'pan',
      'zoom',
      'rotate',
      'trackpad',
      'mouse',
      'view',
    ],
    content: (
      <div className="space-y-2">
        <p>Navigate the 3D scene using your mouse or trackpad:</p>
        <ul className="list-disc space-y-1 pl-4">
          <li>
            <strong>Rotate:</strong> Left-click and drag.
          </li>
          <li>
            <strong>Pan:</strong> Right-click and drag, or Shift + Left-click
            and drag.
          </li>
          <li>
            <strong>Zoom:</strong> Scroll wheel or pinch gesture.
          </li>
          <li>
            <strong>Reset View:</strong> Double-click the background to reset
            the camera.
          </li>
        </ul>
        <p>
          To set up custom lighting or build a presentation-ready product shot,
          see the{' '}
          <BlogLink href="/blog/custom-lighting">Custom Lighting</BlogLink> and{' '}
          <BlogLink href="/blog/product-presentation">
            Product Presentation
          </BlogLink>{' '}
          posts.
        </p>
      </div>
    ),
  },
  {
    id: 'parameters-basics',
    category: 'Parameters',
    title: 'Creating Parameters',
    keywords: ['parameter', 'variable', 'control', 'panel', 'ui', 'input'],
    content: (
      <div className="space-y-2">
        <p>
          Variables defined at the top level of your script automatically become
          customizable parameters. Add comments to customize their appearance in
          the UI.
        </p>
        <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">
          {`// Simple number parameter
width = 10;

// Stop the parser here — helpers stay out of the UI
// --- parameters end ---
module thing() { ... }`}
        </pre>
        <p>
          For a full walkthrough,{' '}
          <BlogLink href="/blog/getting-started">Getting Started</BlogLink>{' '}
          covers the basics and the{' '}
          <BlogLink href="/blog/advanced-parameters">
            Advanced Parameters
          </BlogLink>{' '}
          post covers every supported type.
        </p>
      </div>
    ),
  },
  {
    id: 'parameters-ranges',
    category: 'Parameters',
    title: 'Sliders & Ranges',
    keywords: ['slider', 'range', 'min', 'max', 'step', 'number'],
    content: (
      <div className="space-y-2">
        <p>
          Control number parameters with sliders by defining a range in the
          comment.
        </p>
        <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">
          {`// [min:max]
thickness = 5; // [1:10]

// [min:step:max]
angle = 45; // [0:15:360]

// keyword syntax
wall = 1.6; // min=0.4 max=4 step=0.4`}
        </pre>
        <p>
          See the{' '}
          <BlogLink href="/blog/advanced-parameters">
            Advanced Parameters
          </BlogLink>{' '}
          post for stepped sliders, labelled dropdowns, and every other
          supported annotation.
        </p>
      </div>
    ),
  },
  {
    id: 'parameters-dropdowns',
    category: 'Parameters',
    title: 'Dropdown Menus',
    keywords: ['dropdown', 'select', 'toggle', 'options', 'list', 'enum'],
    content: (
      <div className="space-y-2">
        <p>Create dropdown menus for limited options.</p>
        <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">
          {`// Simple list of options
shape = "box"; // [box, cylinder, sphere]

// Labelled options (Value = Label)
size = 10; // [10=Small, 20=Medium, 50=Large]`}
        </pre>
        <p>
          Up to four short options render as pill toggle buttons; more options
          fall back to a select dropdown. Full details in the{' '}
          <BlogLink href="/blog/advanced-parameters">
            Advanced Parameters
          </BlogLink>{' '}
          post.
        </p>
      </div>
    ),
  },
  {
    id: 'parameters-grouping',
    category: 'Parameters',
    title: 'Grouping Parameters',
    keywords: ['group', 'section', 'accordion', 'category', 'organize'],
    content: (
      <div className="space-y-2">
        <p>
          Organize parameters into collapsible sections using special block
          comments.
        </p>
        <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">
          {`/* [Dimensions] */
width = 10;
height = 20;

/* [Appearance] */
body_color = [1, 0, 0];

// --- parameters end ---`}
        </pre>
        <p>
          The{' '}
          <BlogLink href="/blog/name-tag-tutorial">Name Tag tutorial</BlogLink>{' '}
          shows grouping used alongside ranges and a text input in a complete
          printable model.
        </p>
      </div>
    ),
  },
  {
    id: 'parameters-colors',
    category: 'Parameters',
    title: 'Color Pickers',
    keywords: ['color', 'colour', 'rgb', 'rgba', 'alpha', 'picker', 'swatch'],
    content: (
      <div className="space-y-2">
        <p>
          Name a variable with a <code>_color</code> or <code>_colour</code>{' '}
          suffix and assign it an <code>[R, G, B]</code> or{' '}
          <code>[R, G, B, A]</code> vector to get a color picker. Values are
          floats in the 0–1 range.
        </p>
        <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">
          {`// Opaque color picker
body_color = [0.2, 0.5, 0.9];

// With alpha (transparency)
glass_color = [0.6, 0.85, 1.0, 0.35];`}
        </pre>
        <p>
          See{' '}
          <BlogLink href="/blog/multi-color-models">
            Multi-Color Models
          </BlogLink>{' '}
          for how Meshpit renders multiple colors as separate meshes, and the{' '}
          <BlogLink href="/blog/advanced-parameters">
            Advanced Parameters
          </BlogLink>{' '}
          post for color picker details.
        </p>
      </div>
    ),
  },
  {
    id: 'materials-def',
    category: 'Materials',
    title: 'Defining Materials',
    keywords: [
      'material',
      'pbr',
      'material_def',
      'metalness',
      'roughness',
      'physical',
    ],
    content: (
      <div className="space-y-2">
        <p>
          Meshpit supports advanced PBR (Physically Based Rendering) materials
          using the custom <code>material_def()</code> function. Define a
          material variable and apply it using the <code>material()</code>{' '}
          module. The <code>material()</code> wrapper must be placed outside the{' '}
          <code>color()</code> call.
        </p>
        <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">
          {`// Define a material (also opens a UI editor)
gold = material_def(metalness=1.0, roughness=0.2);

// Apply it — material() wraps color()
material(gold)
  color([0.85, 0.72, 0.38])
    sphere(10, $fn=64);`}
        </pre>
        <p>
          The{' '}
          <BlogLink href="/blog/multi-color-models">
            Multi-Color Models
          </BlogLink>{' '}
          post explains how the rendering pipeline works. The{' '}
          <BlogLink href="/blog/advanced-parameters">
            Advanced Parameters
          </BlogLink>{' '}
          post shows the full material editor UI.
        </p>
      </div>
    ),
  },
  {
    id: 'materials-props',
    category: 'Materials',
    title: 'Material Properties',
    keywords: [
      'clearcoat',
      'sheen',
      'transmission',
      'ior',
      'iridescence',
      'emissive',
      'glass',
      'metal',
    ],
    content: (
      <div className="space-y-2">
        <p>
          The <code>material_def()</code> function accepts the following named
          arguments:
        </p>
        <ul className="list-disc space-y-1 pl-4">
          <li>
            <strong>metalness:</strong> 0.0 (non-metal) to 1.0 (metal).
          </li>
          <li>
            <strong>roughness:</strong> 0.0 (smooth/shiny) to 1.0 (rough/matte).
          </li>
          <li>
            <strong>clearcoat:</strong> 0–1 lacquer/varnish layer.
          </li>
          <li>
            <strong>transmission:</strong> 0–1 physical glass transparency.
          </li>
          <li>
            <strong>ior:</strong> Index of refraction (1.0–2.333; glass ≈ 1.5).
          </li>
          <li>
            <strong>emissive:</strong> [r, g, b] array for glowing color (0–1).
          </li>
          <li>
            <strong>emissive_intensity:</strong> Multiplier for the glow
            brightness.
          </li>
        </ul>
        <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">
          {`// Glowing blue material
neon = material_def(
  emissive=[0, 0.5, 1],
  emissive_intensity=2.0
);

// Glass
lens = material_def(transmission=0.9, ior=1.5, roughness=0.05);`}
        </pre>
        <p>
          For a deep dive into every property and a live demo,{' '}
          <BlogLink href="/blog/multi-color-models">
            Multi-Color Models
          </BlogLink>{' '}
          has the full reference table.
        </p>
      </div>
    ),
  },
  {
    id: 'lighting',
    category: 'Lighting',
    title: 'Custom Lights',
    keywords: [
      'light',
      'lighting',
      'ambient',
      'directional',
      'point',
      'spot',
      'shadow',
      'studio',
    ],
    content: (
      <div className="space-y-2">
        <p>
          Place lights directly in your SCAD code using four built-in modules.
          They produce no printable geometry — they only affect the 3D viewer.
        </p>
        <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">
          {`light_ambient(color=[1,1,1], intensity=0.5);
light_directional(color=[1,1,1], position=[10,10,10], intensity=1.0);
light_point(position=[0,0,30], intensity=1.0, distance=80);
light_spot(position=[40,0,60], angle=30, penumbra=0.3, intensity=1.5);`}
        </pre>
        <p>
          The <BlogLink href="/blog/custom-lighting">Custom Lighting</BlogLink>{' '}
          post covers all four types in detail. The{' '}
          <BlogLink href="/blog/product-presentation">
            Product Presentation
          </BlogLink>{' '}
          post shows a full studio three-point rig alongside export-excluded
          pedestals.
        </p>
      </div>
    ),
  },
  {
    id: 'export-modifiers',
    category: 'Viewer',
    title: 'Display vs. Export Modifiers',
    keywords: [
      'export',
      'stl',
      'exclude',
      'ghost',
      'tilde',
      'percent',
      'display',
      'pedestal',
      'background',
    ],
    content: (
      <div className="space-y-2">
        <p>
          Two modifiers let you show geometry in the viewer while keeping it out
          of exported files:
        </p>
        <ul className="list-disc space-y-1 pl-4">
          <li>
            <code>~</code> — renders with full color in the viewer, never
            exported. Ideal for pedestals and presentation backdrops.
          </li>
          <li>
            <code>%</code> — renders as a semi-transparent blue-grey ghost,
            never exported. Ideal for reference geometry and clearance volumes.
          </li>
        </ul>
        <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">
          {`// Exported part
color([0.8, 0.5, 0.2]) sphere(10, $fn=64);

// Display-only pedestal (full color, not exported)
~color([0.15, 0.15, 0.15]) cylinder(h=4, r=15, $fn=64);

// Ghost reference volume (not exported)
%cube([40, 40, 20], center=true);`}
        </pre>
        <p>
          See{' '}
          <BlogLink href="/blog/multi-color-models">
            Multi-Color Models
          </BlogLink>{' '}
          for the <code>%</code> modifier and{' '}
          <BlogLink href="/blog/product-presentation">
            Product Presentation
          </BlogLink>{' '}
          for practical <code>~</code> pedestal patterns.
        </p>
      </div>
    ),
  },
  {
    id: 'constraints',
    category: 'Viewer',
    title: 'Constraints',
    keywords: [
      'constraint',
      'build volume',
      'keep-out',
      'clearance',
      'violation',
      'warning',
      'ghost',
      'percent',
      'annotation',
      'check',
      'printability',
      'distance',
    ],
    content: (
      <div className="space-y-2">
        <p>
          Attach a <code>// @constraint</code> comment immediately before a{' '}
          <code>%</code> ghost object to turn it into a live geometric check.
          When the constraint is violated an amber warning panel appears in the
          viewer — independently of any compile errors.
        </p>
        <p>Three constraint types are supported:</p>
        <ul className="list-disc space-y-1 pl-4">
          <li>
            <strong>build-volume</strong> — the model must fit entirely inside
            the ghost envelope. Useful for printer bed limits or packaging
            constraints.
          </li>
          <li>
            <strong>keep-out</strong> — no model geometry may enter or intersect
            the ghost region. Useful for mounting bosses, cable runs, or
            reserved assembly space.
          </li>
          <li>
            <strong>clearance</strong> — the model must stay at least{' '}
            <code>distance</code> mm away from the ghost surface. Useful for
            enforcing assembly gaps or press-fit tolerances.
          </li>
        </ul>
        <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">
          {`// @constraint build-volume "Build volume exceeded!"
%cube([200, 200, 200], center=true);

// @constraint keep-out "Avoid the mounting boss"
%translate([30, 0, 0]) cylinder(r=5, h=20);

// @constraint clearance distance=2.5 "Gap too small"
%translate([-30, 0, 0]) sphere(r=15);`}
        </pre>
        <p>
          The optional quoted message replaces the default label in the warning
          panel. Ghost objects with constraints can be hidden via the{' '}
          <strong>Ghost Objects</strong> toggle in the Rendering group of the
          viewer toolbar — the constraint check still runs even when ghosts are
          invisible.
        </p>
      </div>
    ),
  },
  {
    id: 'shortcuts',
    category: 'Shortcuts',
    title: 'Keyboard Shortcuts',
    keywords: [
      'shortcut',
      'keyboard',
      'hotkey',
      'key',
      'f5',
      'ctrl',
      'wireframe',
      'grid',
      'fit',
      'recompile',
      'ai',
      'screenshot',
      'layout',
      'randomize',
      'download',
    ],
    content: (
      <div className="space-y-4 text-sm">
        {(
          [
            {
              heading: 'General',
              rows: [
                { label: 'Recompile / Render', keys: ['F5'] },
                { label: 'Recompile / Render', keys: ['Ctrl', 'Enter'] },
                { label: 'Save Model', keys: ['Ctrl', 'S'] },
                { label: 'Command Palette', keys: ['Ctrl', 'K'] },
                { label: 'Help', keys: ['F1'] },
                { label: 'AI Assistant', keys: ['Ctrl', 'Shift', 'A'] },
                { label: 'Screenshot', keys: ['Ctrl', 'Shift', 'S'] },
                { label: 'Download STL', keys: ['Ctrl', 'D'] },
                { label: 'Layout: Code + Preview', keys: ['Ctrl', '1'] },
                { label: 'Layout: All Panels', keys: ['Ctrl', '2'] },
                { label: 'Layout: Preview + Params', keys: ['Ctrl', '3'] },
                { label: 'Previous File', keys: ['Shift', 'Alt', '←'] },
                { label: 'Next File', keys: ['Shift', 'Alt', '→'] },
              ],
            },
            {
              heading: 'Viewer — always active',
              rows: [{ label: 'Fit model to view', keys: ['F'] }],
            },
            {
              heading: 'Viewer — inactive when editor focused',
              rows: [
                { label: 'Toggle Grid', keys: ['G'] },
                { label: 'Toggle Wireframe', keys: ['W'] },
                { label: 'Toggle Polygon Mode', keys: ['P'] },
                { label: 'Toggle Orthographic / Perspective', keys: ['O'] },
                { label: 'Toggle Auto-Rotate', keys: ['R'] },
                { label: 'Toggle World Axis', keys: ['A'] },
                { label: 'Toggle Object Inspector', keys: ['I'] },
                { label: 'Toggle Excluded Parts', keys: ['E'] },
                { label: 'Toggle Grayscale Mode', keys: ['Shift', 'G'] },
                { label: 'Toggle Measurement Mode', keys: ['Shift', 'M'] },
              ],
            },
            {
              heading: 'Parameters — inactive when editor focused',
              rows: [
                { label: 'Randomize Parameters', keys: ['Shift', 'R'] },
                { label: 'Reset to Default', keys: ['Shift', 'D'] },
              ],
            },
          ] as { heading: string; rows: { label: string; keys: string[] }[] }[]
        ).map((section) => (
          <div key={section.heading}>
            <p className="mb-1.5 font-semibold text-foreground">
              {section.heading}
            </p>
            <div className="space-y-1">
              {section.rows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between gap-4"
                >
                  <span className="text-muted-foreground">{row.label}</span>
                  <span className="flex shrink-0 items-center gap-1">
                    {row.keys.map((k, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && (
                          <span className="text-muted-foreground/50">+</span>
                        )}
                        <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs">
                          {k}
                        </kbd>
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'ai-assistant',
    category: 'AI Assistant',
    title: 'Using AI',
    keywords: [
      'ai',
      'assistant',
      'generate',
      'prompt',
      'natural language',
      'gpt',
    ],
    content: (
      <div className="space-y-2">
        <p>
          Use the AI Assistant to generate new models or modify existing code
          using natural language. Click the "AI Assistant" button in the
          toolbar.
        </p>
        <ul className="list-disc space-y-1 pl-4">
          <li>
            <strong>Create:</strong> Describe a new object (e.g., "A parametric
            gear").
          </li>
          <li>
            <strong>Modify:</strong> Select code or just describe changes (e.g.,
            "Make the walls thicker").
          </li>
        </ul>
      </div>
    ),
  },
];
